import * as types from './mutation-types';
import Vue from 'vue';
import OpenAPI from '@/api/OpenAPI';
import config from '@/properties';
import utils from '@/utils';

const TOAST_TIME = config.TOAST_TIME;
const PLATFORM = config.PLATFORM;

/**
 * Saves location to the state
 * @param  {Object} context Store context
 * @param  {String} data    Data with lat, lon
 */
function saveLocation(context, data) {
  if (!data) throw new Error('No data was given');
  context.commit(types.SAVE_LOCATION, data);
}

/**
 * Saves bounds to the state
 * @param  {Object} context Store context
 * @param  {String} data    Data with lat, lon
 */
function saveBounds(context, data) {
  if (!data) throw new Error('No data was given');
  context.commit(types.SAVE_BOUNDS, data);
}

/**
 * Saves zoom level to the state
 * @param  {Object} context Store context
 * @param  {String} data    Data with lat, lon
 */
function saveZoom(context, data) {
  if (!data) throw new Error('No data was given');
  context.commit(types.SAVE_ZOOM, data);
}

/**
 * Saves network to the state
 * @param  {Object} context Store context
 * @param  {String} data    Data with network name
 */
function saveNetwork(context, data) {
  if (!data) throw new Error('No data was given');
  context.commit(types.SAVE_NETWORK, data);
}

/**
 * Saves technology to the state
 * @param  {Object} context Store context
 * @param  {String} data    Data with tech name
 */
function saveTechnology(context, data) {
  if (!data) throw new Error('No data was given');
  context.commit(types.SAVE_TECHNOLOGY, data);
}

/**
 * Loads static data
 * @param  {Object} context Store context
 */
function loadStaticData(context) {
  OpenAPI.getStatic()
    .then(response => {
      context.commit(types.SAVE_STATIC, response.data);
    })
    .catch(error => {
      console.log(error);
      Vue.toasted.error('Can\'t load application data').goAway(TOAST_TIME);
    });
}

/**
 * Loads legend colors
 * @param  {Object} context Store context
 */
function loadLegend(context) {
  let operator = context.getters.networkName;
  let technology = context.getters.technologyName;
  if (!operator || !technology) return;
  OpenAPI.getLegend({ operator, technology })
    .then(response => {
      context.commit(types.SAVE_LEGEND_COLORS, response.data);
    })
    .catch(error => {
      console.log(error);
      Vue.toasted.error('Can\'t load legend').goAway(TOAST_TIME);
    });
}

/**
 * Loads disclaimer text
 * @param  {Object} context Store context
 */
function loadDisclaimer(context) {
  OpenAPI.getDisclaimer()
    .then(response => {
      context.commit(types.SAVE_DISCLAIMER, response.data);
    })
    .catch(error => {
      console.log(error);
      Vue.toasted.error('Can\'t load disclaimer').goAway(TOAST_TIME);
    });
}

/**
 * Loads faq
 * @param  {Object} context Store context
 */
function loadFaq(context) {
  OpenAPI.getFaq()
    .then(response => {
      context.commit(types.SAVE_FAQ, response.data);
    })
    .catch(error => {
      console.log(error);
      Vue.toasted.error('Can\'t load FAQ').goAway(TOAST_TIME);
    });
}

/**
 * Loads current coverage
 * @param  {Object} context Store context
 */
function loadCurrentCoverage(context) {
  let lat = context.getters.location.lat;
  let lon = context.getters.location.lng;
  let operator = context.getters.networkName;
  let technology = context.getters.technologyName;
  let fromLat = context.getters.bounds.getSouthWest().lat();
  let fromLon = context.getters.bounds.getSouthWest().lng();
  let toLat = context.getters.bounds.getNorthEast().lat();
  let toLon = context.getters.bounds.getNorthEast().lng();
  let zoom = context.getters.zoom;
  if (!operator || !technology || !lat || !lon) return;
  OpenAPI.getCurrentCoverage({
    lat,
    lon,
    operator,
    technology,
    fromLat,
    fromLon,
    toLat,
    toLon,
    zoom
  })
    .then(response => {
      context.commit(types.SAVE_CURRENT_COVERAGE, response.data);
    })
    .catch(error => {
      console.log(error);
      Vue.toasted.error('Can\'t load current coverage').goAway(TOAST_TIME);
    });
}

function screenVisited(context, { screen }) {
  if (!screen) return;
  let version = utils.browserVersion;
  OpenAPI.screenVisited(screen, version, PLATFORM);
}

function sendMapStat(context, data) {
  if (!data) return;
  OpenAPI.sendMapStat(data);
}

function getComparisonAllCoverage(context) {
  let lat = context.getters.location.lat;
  let lon = context.getters.location.lng;
  let technology = context.getters.technologyName;
  let fromLat = context.getters.bounds.getSouthWest().lat();
  let fromLon = context.getters.bounds.getSouthWest().lng();
  let toLat = context.getters.bounds.getNorthEast().lat();
  let toLon = context.getters.bounds.getNorthEast().lng();
  let zoom = context.getters.zoom;

  if (!fromLat || !toLat || !fromLon || !toLon || !zoom || !technology || !lat || !lon) return;

  OpenAPI.getComparisonAllCoverage({
    fromlat: fromLat,
    tolat: toLat,
    fromlon: fromLon,
    tolon: toLon,
    zoom,
    technology,
    lat,
    lon
  }).then((response) => {
    context.commit(types.SAVE_LOCATION_COVERAGE, response.data);
    context.commit(types.CHANGE_LOCATION_COVERAGE_MODAL_STATE, true);
  });
}

/**
 * Saves zoom level to the state
 * @param  {Object} context Store context
 * @param  {Boolean} value
 */
function changeLocationCoverageModalState(context, value = false) {
  context.commit(types.CHANGE_LOCATION_COVERAGE_MODAL_STATE, value);
}

export default {
  saveLocation,
  saveNetwork,
  saveTechnology,
  loadStaticData,
  loadLegend,
  loadFaq,
  loadDisclaimer,
  loadCurrentCoverage,
  screenVisited,
  sendMapStat,
  saveBounds,
  saveZoom,
  getComparisonAllCoverage,
  changeLocationCoverageModalState
};
